﻿import {Children, JSX, memo, useCallback, useMemo, useRef, useState} from "react";
import {MaterialIcon} from "../index";
import {Handler} from "./Handler";
import {WizardContext} from "./WizardContext";
import {WizardProps} from "./WizardProps";
import {WizardStepProps} from "./WizardStep";

type WizardStepTabProps = {
	label: string,
	activeStep: number,
	gotoStep: (stepIndex: number) => void,
	index: number
}

function WizardStepTab(props: WizardStepTabProps): JSX.Element {
	const {label, activeStep, gotoStep, index} = props;
	const isDisabled = index > activeStep;
	const isCompleted = index < activeStep;
	const isCurrent = activeStep === index;

	return <div className={"wizard-steps__step" + (isCompleted ? " wizard-steps__step--completed" : "")}>
		{index > 0 ? <hr/> : undefined}
		<button
			type="button"
			role="tab"
			onClick={isCompleted ? (e) => {
				e.preventDefault();
				gotoStep(index);
			} : (e) => {
				e.preventDefault()
			}}
			tabIndex={isCompleted ? 0 : -1}
			aria-disabled={isDisabled}
			aria-selected={isCurrent}
			aria-controls={`step_${index}`}>
			<span className="wizard-steps__step-number">{isCompleted ? <MaterialIcon icon="check"/> : index + 1}</span>
			<span className="wizard-steps__step-label">{label}</span>
		</button>
	</div>;
}

const MemoizedWizardStepTab: React.FC<WizardStepTabProps> = memo(WizardStepTab);

function Wizard(props: WizardProps): JSX.Element {
	const {header, footer, onStepChange, startIndex} = props;

	const [activeStep, setActiveStep] = useState(startIndex ?? 0);
	const [isLoading, setIsLoading] = useState(false);
	const hasNextStep = useRef(true);
	const hasPreviousStep = useRef(false);
	const nextStepHandler = useRef<Handler>(() => {
	});

	const steps = (Children.toArray(props.children) as React.ReactElement<WizardStepProps>[]).filter(x => !x.props.hidden);
	const stepCount = steps.length;
	const stepIds = useMemo(() => steps.map((_, i) => "step_" + i), [stepCount]);
	const panelIds = useMemo(() => steps.map((_, i) => "panel_" + i), [stepCount]);

	hasNextStep.current = activeStep < stepCount - 1;
	hasPreviousStep.current = activeStep > 0;

	const gotoNextStep = useCallback(() => {
		if (hasNextStep.current) {
			const newActiveStepIndex = activeStep + 1;

			setActiveStep(newActiveStepIndex);
			onStepChange?.(newActiveStepIndex);
		}
	}, [activeStep, onStepChange]);

	const gotoPreviousStep = useCallback(() => {
		if (hasPreviousStep.current) {
			nextStepHandler.current = undefined;
			const newActiveStepIndex = activeStep - 1;

			setActiveStep(newActiveStepIndex);
			onStepChange?.(newActiveStepIndex);
		}
	}, [activeStep, onStepChange]);

	const gotoStep = useCallback(
		(stepIndex: number) => {
			if (stepIndex >= 0 && stepIndex < stepCount) {
				nextStepHandler.current = undefined;
				setActiveStep(stepIndex);
				onStepChange?.(stepIndex);
			}
		},
		[stepCount, onStepChange]
	);

	const handleStep = useRef((handler: Handler) => {
		nextStepHandler.current = handler;
	});

	const doNextStep = useCallback(async () => {
		if (hasNextStep.current && nextStepHandler.current) {
			try {
				setIsLoading(true);
				await nextStepHandler.current();
				setIsLoading(false);
				nextStepHandler.current = undefined;
				gotoNextStep();
			} catch (error) {
				setIsLoading(false);
				throw error;
			}
		} else {
			gotoNextStep();
		}
	}, [gotoNextStep]);

	const wizardValue = useMemo(() => ({
		nextStep: doNextStep,
		previousStep: gotoPreviousStep,
		handleStep: handleStep.current,
		isLoading,
		activeStep,
		stepCount,
		isFirstStep: !hasPreviousStep.current,
		isLastStep: !hasNextStep.current,
		gotoStep
	}), [doNextStep, gotoPreviousStep, isLoading, activeStep, stepCount, gotoStep]);

	const currentStep = steps[activeStep];

	return (
		<WizardContext value={wizardValue}>
			<div className="wizard-steps" role="dialog" aria-labelledby="wizard-title">
				{header}

				<div className="wizard-steps__buttons" role="tablist">
					{steps.map((step, i) =>
						<MemoizedWizardStepTab index={i}
						                       label={step.props.title}
						                       activeStep={activeStep}
						                       gotoStep={gotoStep}
						                       key={step.props.title}/>
					)}
				</div>

				<div role="tabpanel" aria-labelledby={stepIds[activeStep]} id={panelIds[activeStep]}>
					{currentStep.props.children(currentStep.props)}
				</div>

				{footer}
			</div>
		</WizardContext>
	);
}

const MemoizedWizard: React.FC<WizardProps> = memo(Wizard);
export default MemoizedWizard;
